import React from "react";
import { Box, Typography, Button, Divider, SxProps, Theme } from "@mui/material";

interface PageAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: "contained" | "outlined" | "text";
  color?: "primary" | "error" | "inherit";
  disabled?: boolean;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  primaryAction?: PageAction;
  secondaryActions?: PageAction[];
  divider?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * PageHeader — reusable page-level header used across all resource pages.
 * Matches Linear / Stripe / Clerk pattern: Title + description left, CTAs right.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  primaryAction,
  secondaryActions = [],
  divider = false,
  sx,
}) => {
  return (
    <Box component="header" sx={sx}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        mb={divider ? 2.5 : 0}
      >
        {/* Left: Title + Description */}
        <Box>
          <Typography
            variant="h2"
            color="text.primary"
            sx={{ lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {/* Right: Actions */}
        {(primaryAction || secondaryActions.length > 0) && (
          <Box display="flex" alignItems="center" gap={1.5} flexShrink={0}>
            {secondaryActions.map((action, idx) => (
              <Button
                key={idx}
                variant={action.variant ?? "outlined"}
                size="small"
                color={action.color ?? "inherit"}
                startIcon={action.icon}
                onClick={action.onClick}
                disabled={action.disabled}
                sx={{
                  fontWeight: 600,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "13px",
                }}
              >
                {action.label}
              </Button>
            ))}

            {primaryAction && (
              <Button
                variant={primaryAction.variant ?? "contained"}
                size="small"
                color={primaryAction.color ?? "primary"}
                startIcon={primaryAction.icon}
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
                sx={{
                  fontWeight: 600,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "13px",
                  px: 2,
                }}
              >
                {primaryAction.label}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {divider && <Divider />}
    </Box>
  );
};

export default PageHeader;
